import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Box, Card, Center, Container, Flex, Spinner, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { PlanItem, PlannerPlan } from '@/lib/types';
import { baseApi } from '@/utils/api';
import { unwrap } from '@/pages/planner/api';
import PrintButton from '@/components/ui/print-button';
import { useModalValueToggle } from '@/lib/hooks/use-modal-toggle';
import PlannerItemDrawer from '@/pages/planner/item-drawer/planner-item-drawer';
import PlanMetaChips from './view/plan-meta-chips';
import PlanBody from './view/plan-body';
import '@/styles/print.css';

// Unauthenticated share view for a practice plan, reached via the planner
// Share button's /public/planner/:referenceId link. Mirrors the day-plan
// view screen (view-screen.tsx) minus the write actions — the shared plan
// body and item drawer already work without auth (the drawer fetches item
// detail through public/content/:ref).
export default function PublicPlan() {
  const { referenceId } = useParams<{ referenceId: string }>();
  const [plan, setPlan] = useState<PlannerPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const itemDrawer = useModalValueToggle<PlanItem>();

  useEffect(() => {
    let cancelled = false;
    setPlan(null);
    setError(null);
    setIsLoading(true);
    baseApi
      .get(`public/practice-plans/${referenceId}`)
      .then((res) => {
        if (!cancelled) setPlan(unwrap<PlannerPlan>(res));
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(
          err.response?.status === 404
            ? 'This practice plan is no longer available.'
            : (err.response?.data?.error ?? 'Failed to load practice plan.'),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [referenceId]);

  return (
    <Card.Root minH='100vh' mx='auto' width={{ md: '90%' }} maxW='3xl' className='print-card'>
      <Card.Body>
        <Container maxW='container.md'>
          {isLoading && (
            <Center py={10}>
              <Spinner size='xl' />
            </Center>
          )}

          {error && (
            <Center py={10}>
              <Text fontSize='sm' color='fg.muted'>
                {error}
              </Text>
            </Center>
          )}

          {plan && (
            // Same data-print-target dance as view-screen.tsx: drop the
            // attribute while the item drawer is open so the drawer's own
            // print target is the first DOM match.
            <Box {...(itemDrawer.isOpen ? {} : { 'data-print-target': true })}>
              <Flex align='center' justify='space-between' mb={4} flexWrap='wrap' gap={3}>
                <Box>
                  <Flex align='center' gap={2} mb={1}>
                    <Badge colorPalette='brand' size='sm'>
                      {format(new Date(`${plan.plan_date}T00:00:00`), 'MMMM d, yyyy')}
                    </Badge>
                    {plan.from_curriculum && (
                      <Badge variant='outline' size='sm'>
                        From curriculum
                      </Badge>
                    )}
                  </Flex>
                  <Text
                    fontFamily='heading'
                    fontWeight='800'
                    fontSize='h2'
                    textTransform='uppercase'
                    color='fg.strong'
                  >
                    {plan.name}
                  </Text>
                </Box>

                <Flex gap={2} flexWrap='wrap' className='no-print'>
                  <PrintButton documentTitle={plan.name} />
                </Flex>
              </Flex>

              <Box mb={5}>
                <PlanMetaChips startTime={plan.start_time} durationMinutes={plan.duration_minutes} />
              </Box>

              <PlanBody plan={plan} startTime={plan.start_time} onViewItem={itemDrawer.open} />

              <PlannerItemDrawer
                isOpen={itemDrawer.isOpen}
                onClose={itemDrawer.close}
                item={itemDrawer.state}
              />
            </Box>
          )}
        </Container>
      </Card.Body>
    </Card.Root>
  );
}
